// =============================================================================
// MeuDriver — Hook: useStudentBadges (Batch S3)
// Sistema de emblemas (badges) do estudante
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BadgeDefinition {
  id: string
  name: string
  description: string | null
  icon_emoji: string
  trigger_type: string
  trigger_value: number
  is_active: boolean
}

export interface StudentBadge {
  id: string
  student_id: string
  badge_id: string
  earned_at: string
  badge_definition?: BadgeDefinition
}

export interface BadgeWithStatus extends BadgeDefinition {
  earned: boolean
  earned_at: string | null
}

export interface CheckAndGrantResult {
  badge_name: string
  just_granted: boolean
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const badgeKeys = {
  definitions: ['badge-definitions'] as const,
  student:     (studentId: string) => ['student-badges', studentId] as const,
}

// ─── useAllBadgeDefinitions ───────────────────────────────────────────────────

export function useAllBadgeDefinitions() {
  return useQuery<BadgeDefinition[]>({
    queryKey: badgeKeys.definitions,
    staleTime: 1000 * 60 * 30, // badges mudam raramente
    queryFn: async () => {
      const { data, error } = await supabase
        .from('badge_definitions')
        .select('*')
        .eq('is_active', true)
        .order('trigger_value', { ascending: true })

      if (error) throw new Error(error.message)
      return (data ?? []) as BadgeDefinition[]
    },
  })
}

// ─── useStudentBadges ─────────────────────────────────────────────────────────
// Retorna badges com status earned/not-earned para um estudante.
// Se studentId não for passado, usa o usuário logado.

export function useStudentBadges(studentId?: string) {
  const { user } = useAuth()
  const targetId = studentId ?? user?.id

  const { data: definitions = [] } = useAllBadgeDefinitions()

  return useQuery<BadgeWithStatus[]>({
    queryKey: badgeKeys.student(targetId ?? ''),
    enabled:  Boolean(targetId),
    staleTime: 1000 * 60 * 2,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_badges')
        .select('badge_id, earned_at')
        .eq('student_id', targetId!)

      if (error) throw new Error(error.message)

      const earnedMap = new Map<string, string>(
        (data ?? []).map((sb) => [sb.badge_id, sb.earned_at]),
      )

      return definitions.map((def): BadgeWithStatus => ({
        ...def,
        earned:    earnedMap.has(def.id),
        earned_at: earnedMap.get(def.id) ?? null,
      }))
    },
  })
}

// ─── useCheckAndGrantBadges ───────────────────────────────────────────────────

export function useCheckAndGrantBadges() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation<CheckAndGrantResult[], Error, string | undefined>({
    mutationFn: async (studentId?: string) => {
      const targetId = studentId ?? user?.id
      if (!targetId) throw new Error('Usuário não autenticado.')

      const { data, error } = await supabase.rpc('check_and_grant_badges', {
        p_student_id: targetId,
      })

      if (error) throw new Error(error.message)
      return (data ?? []) as CheckAndGrantResult[]
    },
    onSuccess: (results, studentId) => {
      const targetId = studentId ?? user?.id
      if (targetId) {
        queryClient.invalidateQueries({ queryKey: badgeKeys.student(targetId) })
      }
      // Retorna results para o caller poder exibir toasts
      return results
    },
  })
}
